import React, { MutableRefObject, useImperativeHandle } from 'react';
import { ModalsKeys, Nullable } from 'global';
import { useModal } from 'hooks/useModal';
import { Quote } from 'modules/quote/types';
import { Button, Stack } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { AddCircle } from '@mui/icons-material';
import { QuoteFeeType } from '@trnsact/trnsact-shared-types';
import { ListingForm } from 'components/shared/TableListing/types';
import { Listing, useListing } from 'components/shared/TableListing';
import { chargeTypesByLabels, newChargeRow } from '../../constants';
import { Charge, ChargeAttachType, ChargeListingContext } from '../../types';
import { chargesListingColumns } from './chargesListingConfig';

interface Props {
  quote: Nullable<Quote>;
  equipmentId: string | undefined;
  submitRef: MutableRefObject<{ submit: () => ListingForm<Charge> } | null>;
}

export const ChargesListing = ({ quote, submitRef, equipmentId }: Props) => {
  const { handleOpen, handleClose } = useModal(ModalsKeys.ChargesEquipmentDialog);
  const { handleOpen: onOpenCreateChargeDialog } = useModal(ModalsKeys.CreateChargesEquipmentDialog);

  const { columns, handleAddRow, tableData, form, handleRemoveRow } = useListing<Charge, ChargeListingContext>(
    {
      headerColumns: chargesListingColumns,
      bodyFields: (quote?.fees ?? [])
        .filter(fee => fee.equipmentId === equipmentId)
        .map(fee => ({
          ...newChargeRow,
          type: fee.type,
          label: fee.description,
          wholesalePrice: fee.amount,
          financeType: chargeTypesByLabels[fee.description],
          attachTo: {
            id: equipmentId!,
            type: ChargeAttachType.Equipment,
          },
        })),
    },
    {
      shouldUnregister: false,
    }
  );

  useImperativeHandle(submitRef, () => ({
    submit: () => form.getValues(),
  }));

  const handleAddCharge = () => {
    handleOpen({
      onOpenCreateChargeDialog: () => {
        onOpenCreateChargeDialog({
          onConfirm: newCharge => {
            handleAddRow({
              financeType: 'other',
              label: newCharge.name,
              type: QuoteFeeType.Other,
              ...newChargeRow,
              attachTo: {
                id: equipmentId!,
                type: ChargeAttachType.Equipment,
              },
            });
            handleClose();
          },
        });
      },
      onConfirm: charges => {
        charges.forEach(charge =>
          handleAddRow({
            ...charge,
            attachTo: { id: equipmentId!, type: ChargeAttachType.Equipment },
          })
        );
        handleClose();
      },
    });
  };

  return (
    <FormProvider {...form}>
      <Listing<Charge, ChargeListingContext>
        columns={columns}
        injectContext={{ form, onRemoveRow: handleRemoveRow }}
        tableData={tableData}
        actionPanelBottom={() => (
          <Stack direction="row" justifyContent="space-between">
            <Button onClick={handleAddCharge} startIcon={<AddCircle />} size="small">
              Add equipment charges
            </Button>
          </Stack>
        )}
      />
    </FormProvider>
  );
};
